import { useQuery } from '@tanstack/vue-query';
import { useExperienceApi } from '@/shared/api/useExperienceApi.ts';
import { computed, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { createExperienceAggregationsFromResource } from '@/entities/experience/aggregation/model/create-experience-aggregations-from-resource.ts';
import type { IExperienceAggregations } from '@/entities/experience/aggregation/model/types.ts';
import type { TMovementType } from '@/entities/movement-type';
import { useUnlicensedContentFilter } from '@/shared/composables/useUnlicensedContentFilter.ts';
import { useLoaderStore } from '@/shared/store/useLoaderStore';
import { normalizeExcursionAggregations } from './normalizeExcursionAggregations.ts';

// TODO: сделать рефактор после разделения сторов (и синхронизации с турами)
export interface ExcursionParams {
  city__slug?: MaybeRefOrGetter<string | null>;
  type?: MaybeRefOrGetter<string>;
  start_price?: MaybeRefOrGetter<number | null>;
  end_price?: MaybeRefOrGetter<number | null>;
  start_duration?: MaybeRefOrGetter<number | null>;
  end_duration?: MaybeRefOrGetter<number | null>;
  start_date?: MaybeRefOrGetter<string | null>;
  end_date?: MaybeRefOrGetter<string | null>;
  movement_type?: MaybeRefOrGetter<string | null>;
  persons_count?: MaybeRefOrGetter<number>;
  page?: MaybeRefOrGetter<number>;
  tag__slugs?: MaybeRefOrGetter<string | null>;
  sorting?: MaybeRefOrGetter<string | null>;
  date_filter_mode?: MaybeRefOrGetter<string>;
  bookable?: MaybeRefOrGetter<boolean>;
  start_time_interval?: MaybeRefOrGetter<string>;
  duration_interval?: MaybeRefOrGetter<string>;
  price_format?: MaybeRefOrGetter<string>;
  children_allowed?: MaybeRefOrGetter<boolean | undefined>;
}

export const useExcursionExperienceAggregations = (params: ComputedRef<ExcursionParams>) => {
  const loaderStore = useLoaderStore();

  const { getExperienceAggregations } = useExperienceApi();
  const { hideUnlicensed } = useUnlicensedContentFilter();

  const normalizedParams = computed(() => ({
    ...normalizeExcursionAggregations(params.value),
    exclude_needs_license: hideUnlicensed.value,
  }));

  const { data: experienceAggregationsData, isFetching: isExperienceAggregationsPending } = useQuery({
    queryKey: computed(() => ['experienceAggregations', 'excursion', normalizedParams.value]),
    queryFn: async () => {
      const response = await getExperienceAggregations({
        query: normalizedParams.value,
      });

      if (response?.response?.status === 400) {
        loaderStore.is404 = true;
      }

      if (response?.data) {
        return createExperienceAggregationsFromResource(response.data);
      }
    },
    placeholderData: (prevData) => prevData || null,
    refetchOnWindowFocus: false,
  });

  const createComputedField = <T extends keyof IExperienceAggregations>(key: T) =>
    computed(() => experienceAggregationsData.value?.[key] ?? null);

  const processedMovementType = computed(() => {
    const movementTypeData = experienceAggregationsData.value?.movementType;

    if (!movementTypeData || typeof movementTypeData !== 'object') {
      return null;
    }

    const result: Partial<Record<TMovementType, number>> = {};

    for (const key in movementTypeData) {
      if (Object.prototype.hasOwnProperty.call(movementTypeData, key)) {
        const value = movementTypeData[key as keyof typeof movementTypeData];
        if (typeof value === 'number') {
          result[key as TMovementType] = value;
        }
      }
    }

    return Object.keys(result).length > 0 ? (result as Record<TMovementType, number>) : null;
  });

  return {
    tags: createComputedField('tags'),
    tagsSet: createComputedField('tagsSet'),
    type: createComputedField('type'),
    customType: createComputedField('customType'),
    movementType: processedMovementType,
    price: createComputedField('price'),
    duration: createComputedField('duration'),
    date: createComputedField('date'),
    availability: createComputedField('availability'),

    isExperienceAggregationsPending,
  };
};
